import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

function parseBxgyProductIds(value) {
  const ids = (value || '')
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isFinite(id) && id > 0);

  return [...new Set(ids)];
}

function getBxgyRules() {
  const body = document.body;

  return [
    {
      id: '1',
      enabled: body?.dataset.bxgyEnabled === 'true',
      giftVariantId: Number(body?.dataset.bxgyVariantId || 0),
      giftSellingPlanId: Number(body?.dataset.bxgySellingPlanId || 0),
      mainSellingPlanId: Number(body?.dataset.bxgyMainSellingPlanId || 0),
      triggerProductIds: parseBxgyProductIds(body?.dataset.bxgyProductIds),
    },
    {
      id: '2',
      enabled: body?.dataset.bxgy2Enabled === 'true',
      giftVariantId: Number(body?.dataset.bxgy2VariantId || 0),
      giftSellingPlanId: Number(body?.dataset.bxgy2SellingPlanId || 0),
      mainSellingPlanId: Number(body?.dataset.bxgy2MainSellingPlanId || 0),
      triggerProductIds: parseBxgyProductIds(body?.dataset.bxgy2ProductIds),
    },
  ].filter((rule) => (
    rule.enabled &&
    rule.giftVariantId &&
    rule.giftSellingPlanId &&
    rule.triggerProductIds.length
  ));
}

function getMatchingBxgyRules(productId, variantId) {
  const productIdNumber = Number(productId);
  const variantIdNumber = Number(variantId);

  return getBxgyRules().filter((rule) => (
    variantIdNumber !== rule.giftVariantId &&
    rule.triggerProductIds.includes(productIdNumber)
  ));
}

function getCartSectionIds() {
  const sectionIds = [];

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });

  return sectionIds;
}

async function getCartJson() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) return null;
  return response.json();
}

function cartHasBxgyTrigger(cart, rule) {
  return cart.items.some((item) =>
    rule.triggerProductIds.includes(Number(item.product_id))
  );
}

function getBxgyGiftLine(cart, rule) {
  return cart.items.find((item) => {
    const itemVariantId = Number(item.id);
    const itemSellingPlanId = Number(item.selling_plan_allocation?.selling_plan?.id || 0);
    const isBxgyGift = item.properties?._bxgy_gift === 'true';
    const giftRuleId = String(item.properties?._bxgy_rule || '1');

    return (
      itemVariantId === Number(rule.giftVariantId) &&
      itemSellingPlanId === Number(rule.giftSellingPlanId) &&
      isBxgyGift &&
      giftRuleId === String(rule.id)
    );
  });
}

async function cartHasBxgyGift(rule) {
  const cart = await getCartJson();
  if (!cart) return false;

  return Boolean(getBxgyGiftLine(cart, rule));
}

async function addBxgyGiftVariant(rule, sectionIds = []) {
  if (!rule?.giftVariantId || !rule?.giftSellingPlanId) return null;

  const alreadyInCart = await cartHasBxgyGift(rule);
  if (alreadyInCart) return null;

  const formData = new FormData();
  formData.append('id', String(rule.giftVariantId));
  formData.append('quantity', '1');
  formData.append('selling_plan', String(rule.giftSellingPlanId));
  formData.append('properties[_bxgy_gift]', 'true');
  formData.append('properties[_bxgy_rule]', String(rule.id));

  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
  }

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/cart/add.js', true);
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.onload = () => {
      let data = null;

      try {
        data = JSON.parse(xhr.responseText);
      } catch (error) {
        data = { raw: xhr.responseText };
      }

      if (xhr.status < 200 || xhr.status >= 300 || data.status) {
        console.error('BXGY add failed:', {
          ruleId: rule.id,
          status: xhr.status,
          data,
        });
        resolve(null);
        return;
      }

      resolve(data);
    };

    xhr.onerror = () => {
      console.error('BXGY add network error');
      resolve(null);
    };

    xhr.send(formData);
  });
}

async function removeBxgyGiftsIfNoTriggers(sectionIds = []) {
  const rules = getBxgyRules();
  if (!rules.length) return null;

  const cart = await getCartJson();
  if (!cart) return null;

  let latestResponse = null;

  for (const rule of rules) {
    const hasTrigger = cartHasBxgyTrigger(cart, rule);
    const giftLine = getBxgyGiftLine(cart, rule);

    if (hasTrigger || !giftLine) continue;

    const formData = new FormData();
    formData.append('id', giftLine.key);
    formData.append('quantity', '0');

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    latestResponse = await new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/cart/change.js', true);
      xhr.setRequestHeader('Accept', 'application/json');

      xhr.onload = () => {
        let data = null;

        try {
          data = JSON.parse(xhr.responseText);
        } catch (error) {
          data = { raw: xhr.responseText };
        }

        if (xhr.status < 200 || xhr.status >= 300 || data.status) {
          console.error('BXGY remove failed:', {
            ruleId: rule.id,
            status: xhr.status,
            data,
          });
          resolve(null);
          return;
        }

        document.dispatchEvent(
          new CartAddEvent({}, giftLine.key, {
            source: 'bxgy-remove',
            itemCount: data.item_count || 0,
            sections: data.sections,
          })
        );

        resolve(data);
      };

      xhr.onerror = () => {
        console.error('BXGY remove network error');
        resolve(null);
      };

      xhr.send(formData);
    });
  }

  return latestResponse;
}

let bxgyCleanupTimeout;

function scheduleBxgyGiftCleanup() {
  clearTimeout(bxgyCleanupTimeout);

  bxgyCleanupTimeout = setTimeout(() => {
    removeBxgyGiftsIfNoTriggers(getCartSectionIds());
  }, 350);
}

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    addToCartButton.classList.add('atc-added');

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

class ProductFormComponent extends Component {
  requiredRefs = ['variantId'];

  #abortController = new AbortController();
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;

    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const cartItemComponentsSectionIds = getCartSectionIds();

    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const matchingBxgyRulesBeforeAdd = getMatchingBxgyRules(this.dataset.productId, formData.get('id'));
    const mainSellingPlanRule = matchingBxgyRulesBeforeAdd.find((rule) => Number(rule.mainSellingPlanId) > 0);

    if (!formData.get('selling_plan') && mainSellingPlanRule) {
      formData.append('selling_plan', String(mainSellingPlanRule.mainSellingPlanId));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const rawResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      const response = await rawResponse.json();

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
        );

        if (!addToCartTextError) return;

        addToCartTextError.classList.remove('hidden');

        const textNode = addToCartTextError.childNodes[2];

        if (textNode) {
          textNode.textContent = response.message;
        } else {
          addToCartTextError.appendChild(document.createTextNode(response.message));
        }

        this.#setLiveRegionText(response.message);

        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, 10000);

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          })
        );

        return;
      }

      const id = formData.get('id');

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      if (!id) throw new Error('Form ID is required');

      if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
        const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
        const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      }

      let finalSections = response.sections;

      try {
        const triggerProductId = Number(response.product_id || this.dataset.productId);
        const addedVariantId = Number(response.variant_id || id);
        const matchingBxgyRules = getMatchingBxgyRules(triggerProductId, addedVariantId);

        for (const rule of matchingBxgyRules) {
          const giftResponse = await addBxgyGiftVariant(rule, cartItemComponentsSectionIds);

          if (giftResponse?.sections) {
            finalSections = giftResponse.sections;
          }
        }

        const removeGiftResponse = await removeBxgyGiftsIfNoTriggers(cartItemComponentsSectionIds);

        if (removeGiftResponse?.sections) {
          finalSections = removeGiftResponse.sections;
        }
      } catch (error) {
        console.error('BXGY error:', error);
      }

      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
          productId: response.product_id || this.dataset.productId,
          sections: finalSections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      try {
        cartPerformance.measureFromEvent('add:user-action', event);
      } catch (error) {
        console.warn('Cart performance mark missing:', error);
      }
    }
  }

  #getLiveRegion() {
    let liveRegion = this.refs.liveRegion || this.querySelector('[ref="liveRegion"]');

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('ref', 'liveRegion');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.className = 'visually-hidden';
      this.appendChild(liveRegion);
    }

    return liveRegion;
  }

  #setLiveRegionText(text) {
    this.#getLiveRegion().textContent = text;
  }

  #clearLiveRegionText() {
    this.#getLiveRegion().textContent = '';
  }

  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    variantId.value = event.detail.resource?.id ?? '';

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
      }
    }
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  source;
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);
      this.style.setProperty('--scale', `${1 - progress * 0.5}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  return {
    x: aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x,
    y: aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y,
  };
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleBxgyGiftCleanup);

document.addEventListener('change', (event) => {
  if (event.target?.closest?.('cart-items-component')) {
    scheduleBxgyGiftCleanup();
  }
});

document.addEventListener('click', (event) => {
  if (event.target?.closest?.('cart-items-component')) {
    scheduleBxgyGiftCleanup();
  }
});

document.addEventListener('cart:update', scheduleBxgyGiftCleanup);
document.addEventListener('cart:updated', scheduleBxgyGiftCleanup);
document.addEventListener('line-item:removed', scheduleBxgyGiftCleanup);

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}